import { useEffect, useMemo, useState } from 'react';
import React from 'react';
import mortgageData from './mortgageData';
import {
  buildSeries,
  buildScenarioForecasts,
  buildSummary,
  formatMoney,
  formatMonthLabel,
  parseCsvSnapshots,
} from './model';
import './mortgageJourney.css';

function ScoreRing({ value, label, tone }) {
  const pct = Math.max(0, Math.min(value, 100));
  const radius = 70;
  const c = 2 * Math.PI * radius;
  const dashOffset = c * (1 - pct / 100);

  return (
    <div className="mj-ring">
      <svg viewBox="0 0 180 180" role="img" aria-label={`${label}: ${pct.toFixed(1)} percent`}>
        <circle cx="90" cy="90" r={radius} className="mj-ring__track" />
        <circle
          cx="90"
          cy="90"
          r={radius}
          className={`mj-ring__value mj-ring__value--${tone}`}
          strokeDasharray={c}
          strokeDashoffset={dashOffset}
        />
        <text x="90" y="92" textAnchor="middle" className="mj-ring__centerValue">{pct.toFixed(1)}%</text>
        <text x="90" y="112" textAnchor="middle" className="mj-ring__centerLabel">{label}</text>
      </svg>
    </div>
  );
}

function MilestoneIcon({ type }) {
  if (type === 'offset-boost') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M4 10.5 12 4l8 6.5V20a1 1 0 0 1-1 1h-5v-6h-4v6H5a1 1 0 0 1-1-1z" />
      </svg>
    );
  }

  if (type === 'milestone') {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        <path d="M6 7h12v2H6zM7 9h10l-1 9H8zM10 6a2 2 0 0 1 4 0v1h-1.8V6a.2.2 0 0 0-.4 0v1H10z" />
      </svg>
    );
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
      <path d="m12 3 2.5 5.1L20 9l-4 3.9.9 5.6L12 16l-4.9 2.5.9-5.6L4 9l5.5-.9z" />
    </svg>
  );
}

function BalanceTrendChart({ series, currency }) {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const width = 920;
  const height = 310;
  const pad = { top: 20, right: 30, bottom: 36, left: 70 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const maxValue = Math.max(...series.map((d) => d.loanBalance));
  const yScale = (value) => pad.top + innerH - (value / maxValue) * innerH;
  const xScale = (idx) => pad.left + (idx / (series.length - 1 || 1)) * innerW;

  const buildPath = (key) =>
    series
      .map((row, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(idx).toFixed(1)} ${yScale(row[key]).toFixed(1)}`)
      .join(' ');

  const loanPath = buildPath('loanBalance');
  const effectivePath = buildPath('effectiveBalance');
  const ticks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => Math.round(maxValue * ratio));
  const activeIndex = hoveredIndex === null ? series.length - 1 : hoveredIndex;
  const activePoint = series[activeIndex];
  const tooltipWidth = 236;
  const tooltipHeight = 72;
  const tooltipX = activePoint
    ? Math.min(Math.max(xScale(activeIndex) - tooltipWidth / 2, pad.left), width - pad.right - tooltipWidth)
    : 0;
  const tooltipY = activePoint
    ? Math.max(yScale(activePoint.effectiveBalance) - tooltipHeight - 12, pad.top + 4)
    : 0;

  return (
    <div className="mj-chartCard">
      <div className="mj-chartCard__header">
        <h3>Balance Trend</h3>
        <p>Raw loan balance vs offset-adjusted effective balance.</p>
      </div>
      <svg
        className="mj-chart"
        viewBox={`0 0 ${width} ${height}`}
        role="img"
        aria-label="Balance trend chart"
        onMouseLeave={() => setHoveredIndex(null)}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={pad.left}
              y1={yScale(tick)}
              x2={width - pad.right}
              y2={yScale(tick)}
              className="mj-grid"
            />
            <text x={pad.left - 10} y={yScale(tick) + 4} className="mj-axisLabel" textAnchor="end">
              {formatMoney(tick, currency)}
            </text>
          </g>
        ))}

        <path d={loanPath} className="mj-line mj-line--loan" />
        <path d={effectivePath} className="mj-line mj-line--effective" />

        {activePoint ? (
          <line
            x1={xScale(activeIndex)}
            y1={pad.top}
            x2={xScale(activeIndex)}
            y2={height - pad.bottom}
            className="mj-hoverGuide"
          />
        ) : null}

        {activePoint ? (
          <g className="mj-chartTooltip" transform={`translate(${tooltipX}, ${tooltipY})`}>
            <rect width={tooltipWidth} height={tooltipHeight} rx="10" ry="10" />
            <text x="12" y="22" className="mj-chartTooltip__title">
              {new Date(`${activePoint.month}-01`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })}
            </text>
            <text x="12" y="42" className="mj-chartTooltip__line">
              Loan: {formatMoney(activePoint.loanBalance, currency)}
            </text>
            <text x="12" y="60" className="mj-chartTooltip__line">
              Effective: {formatMoney(activePoint.effectiveBalance, currency)}
            </text>
          </g>
        ) : null}

        {series.map((row, idx) => (
          <g key={row.month}>
            <rect
              x={idx === 0 ? pad.left : (xScale(idx - 1) + xScale(idx)) / 2}
              y={pad.top}
              width={
                idx === series.length - 1
                  ? width - pad.right - (idx === 0 ? pad.left : (xScale(idx - 1) + xScale(idx)) / 2)
                  : (xScale(idx) + xScale(idx + 1)) / 2 - (idx === 0 ? pad.left : (xScale(idx - 1) + xScale(idx)) / 2)
              }
              height={innerH}
              fill="transparent"
              onMouseEnter={() => setHoveredIndex(idx)}
            />
            <circle cx={xScale(idx)} cy={yScale(row.loanBalance)} r={activeIndex === idx ? '5' : '4'} className="mj-dot mj-dot--loan" />
            <circle
              cx={xScale(idx)}
              cy={yScale(row.effectiveBalance)}
              r={activeIndex === idx ? '5' : '4'}
              className="mj-dot mj-dot--effective"
            />
            <text x={xScale(idx)} y={height - 12} className="mj-axisLabel" textAnchor="middle">
              {formatMonthLabel(row.month)}
            </text>
          </g>
        ))}
      </svg>
      <div className="mj-legend">
        <span><i className="swatch swatch--loan" />Loan balance</span>
        <span><i className="swatch swatch--effective" />Effective balance (after offset)</span>
      </div>
    </div>
  );
}

function MonthlyImpactChart({ series, currency }) {
  const monthly = series.map((row, i) => {
    if (i === 0) {
      return { month: row.month, principalDrop: 0, offsetGain: 0 };
    }
    const prev = series[i - 1];
    return {
      month: row.month,
      principalDrop: Math.max(prev.loanBalance - row.loanBalance, 0),
      offsetGain: Math.max(row.offsetBalance - prev.offsetBalance, 0),
    };
  });

  const maxBar = Math.max(...monthly.map((m) => m.principalDrop + m.offsetGain), 1);

  return (
    <div className="mj-chartCard">
      <div className="mj-chartCard__header">
        <h3>Monthly Impact</h3>
        <p>How each month moved the needle through principal paydown and offset growth.</p>
      </div>
      <div className="mj-impactGrid">
        {monthly.map((row) => {
          const total = row.principalDrop + row.offsetGain;
          const principalPct = (row.principalDrop / maxBar) * 100;
          const offsetPct = (row.offsetGain / maxBar) * 100;
          return (
            <div key={row.month} className="mj-impactRow">
              <div className="mj-impactRow__meta">
                <span>{formatMonthLabel(row.month)}</span>
                <strong>{formatMoney(total, currency)}</strong>
              </div>
              <div className="mj-impactRow__bar">
                <span style={{ width: `${principalPct}%` }} className="mj-impactRow__barPrincipal" />
                <span style={{ width: `${offsetPct}%` }} className="mj-impactRow__barOffset" />
              </div>
            </div>
          );
        })}
      </div>
      <div className="mj-legend">
        <span><i className="swatch swatch--principal" />Principal reduction</span>
        <span><i className="swatch swatch--offset" />Offset increase</span>
      </div>
    </div>
  );
}

function ForecastChart({ scenarios, currency }) {
  const [selectedIdx, setSelectedIdx] = useState(0);
  const width = 920;
  const height = 320;
  const pad = { top: 16, right: 20, bottom: 36, left: 70 };
  const innerW = width - pad.left - pad.right;
  const innerH = height - pad.top - pad.bottom;

  const allPoints = scenarios.flatMap((scenario) => scenario.points);
  const maxY = Math.max(...allPoints.map((p) => p.effectiveBalance), 1);
  const maxLen = Math.max(...scenarios.map((s) => s.points.length), 1);
  const activeIndex = Math.min(selectedIdx, maxLen - 1);
  const baseTimeline = scenarios[0]?.points || [];

  const xScale = (idx) => pad.left + (idx / Math.max(maxLen - 1, 1)) * innerW;
  const yScale = (value) => pad.top + innerH - (value / maxY) * innerH;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => Math.round(maxY * r));
  const xTickIndexes = Array.from(
    new Set([0, Math.floor((maxLen - 1) * 0.25), Math.floor((maxLen - 1) * 0.5), Math.floor((maxLen - 1) * 0.75), maxLen - 1])
  );

  const selectedMonthLabel = baseTimeline[activeIndex]
    ? new Date(`${baseTimeline[activeIndex].month}-01`).toLocaleDateString('en-AU', {
      month: 'long',
      year: 'numeric',
    })
    : 'N/A';

  return (
    <div className="mj-chartCard">
      <div className="mj-chartCard__header">
        <h3>Scenario Forecast</h3>
        <p>
          {scenarios[0]?.useInterestModel
            ? 'Projected using annual rate with daily compounding and your minimum monthly repayment. '
            : ''}
          Projected effective balance across your three monthly offset growth scenarios.
        </p>
      </div>
      <svg className="mj-chart" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Forecast scenario chart">
        {yTicks.map((tick) => (
          <g key={tick}>
            <line x1={pad.left} y1={yScale(tick)} x2={width - pad.right} y2={yScale(tick)} className="mj-grid" />
            <text x={pad.left - 10} y={yScale(tick) + 4} className="mj-axisLabel" textAnchor="end">
              {formatMoney(tick, currency)}
            </text>
          </g>
        ))}

        <line
          x1={xScale(activeIndex)}
          y1={pad.top}
          x2={xScale(activeIndex)}
          y2={height - pad.bottom}
          className="mj-forecastGuide"
        />

        {scenarios.map((scenario) => {
          const path = scenario.points
            .map((point, idx) => `${idx === 0 ? 'M' : 'L'} ${xScale(idx).toFixed(1)} ${yScale(point.effectiveBalance).toFixed(1)}`)
            .join(' ');

          return <path key={scenario.id} d={path} className="mj-line" style={{ stroke: scenario.color }} />;
        })}

        {xTickIndexes.map((tickIdx) => {
          const point = baseTimeline[tickIdx];
          if (!point) {
            return null;
          }
          return (
            <text key={`tick-${tickIdx}`} x={xScale(tickIdx)} y={height - 10} className="mj-axisLabel" textAnchor="middle">
              {formatMonthLabel(point.month)}
            </text>
          );
        })}

        {scenarios.map((scenario) => {
          const point = scenario.points[activeIndex] || scenario.points[scenario.points.length - 1];
          const pointIndex = scenario.points[activeIndex] ? activeIndex : scenario.points.length - 1;
          return (
            <g key={`${scenario.id}-active`}>
              <circle cx={xScale(pointIndex)} cy={yScale(point.effectiveBalance)} r="5" style={{ fill: scenario.color }} />
            </g>
          );
        })}
      </svg>
      <div className="mj-forecastScrubber">
        <div className="mj-forecastScrubber__header">
          <strong>{selectedMonthLabel}</strong>
          <span>Drag to inspect forecast date and remaining balance</span>
        </div>
        <input
          type="range"
          min={0}
          max={Math.max(maxLen - 1, 0)}
          value={activeIndex}
          onChange={(e) => setSelectedIdx(Number(e.target.value))}
        />
        <div className="mj-forecastValues">
          {scenarios.map((scenario) => {
            const point = scenario.points[activeIndex] || scenario.points[scenario.points.length - 1];
            return (
              <div key={`${scenario.id}-value`}>
                <span><i className="swatch" style={{ background: scenario.color }} />{scenario.label}</span>
                <strong>{formatMoney(point.effectiveBalance, currency)}</strong>
              </div>
            );
          })}
        </div>
      </div>
      <div className="mj-legend">
        {scenarios.map((scenario) => (
          <span key={scenario.id}><i className="swatch" style={{ background: scenario.color }} />{scenario.label}</span>
        ))}
      </div>
    </div>
  );
}

function PinGate({ onUnlock }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');

  const submit = (e) => {
    e.preventDefault();
    if (pin === '585858') {
      localStorage.setItem('mortgage_journey_pin_ok', 'true');
      onUnlock();
      return;
    }
    setError('Incorrect PIN.');
  };

  return (
    <div className="mj-lockShell">
      <form className="mj-lockCard" onSubmit={submit}>
        <p>Private Dev Page</p>
        <h1>Mortgage Journey</h1>
        <label htmlFor="pin-input">Enter PIN</label>
        <input
          id="pin-input"
          type="password"
          inputMode="numeric"
          autoComplete="off"
          maxLength={6}
          value={pin}
          onChange={(e) => {
            setPin(e.target.value.replace(/\D/g, ''));
            setError('');
          }}
          placeholder="6 digits"
        />
        {error ? <p className="mj-lockError">{error}</p> : null}
        <button type="submit">Unlock</button>
      </form>
    </div>
  );
}

function MonthlyEntryForm({ onAdd }) {
  const [form, setForm] = useState({
    month: '',
    loanBalance: '',
    offsetBalance: '',
    requiredRepayment: '',
    extraRepayment: '',
    interestPaid: '',
    note: '',
  });
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!/^\d{4}-\d{2}$/.test(form.month)) {
      setError('Month must be in YYYY-MM format');
      return;
    }
    if (!form.loanBalance || !form.offsetBalance) {
      setError('Loan and Offset balances are required');
      return;
    }
    setError('');
    onAdd({
      ...form,
      loanBalance: Number(form.loanBalance),
      offsetBalance: Number(form.offsetBalance),
      requiredRepayment: Number(form.requiredRepayment),
      extraRepayment: Number(form.extraRepayment),
      interestPaid: Number(form.interestPaid),
    });
    setForm({ month: '', loanBalance: '', offsetBalance: '', requiredRepayment: '', extraRepayment: '', interestPaid: '', note: '' });
  };

  // Two-column layout, styled to match app theme
  return (
    <form className="mj-monthlyEntryForm" onSubmit={handleSubmit} style={{
      maxWidth: 600,
      margin: '2rem auto',
      background: 'var(--mj-bg, #181c24)',
      borderRadius: 16,
      boxShadow: '0 2px 16px #0002',
      padding: 32,
      color: 'var(--mj-fg, #fff)',
    }}>
      <h2 style={{ marginBottom: 24, color: 'var(--mj-accent, #1f7a73)' }}>Add Monthly Data</h2>
      <div style={{ display: 'grid', gridTemplateColumns: '220px 1fr', gap: '18px 24px', alignItems: 'center' }}>
        <label style={{ textAlign: 'right', fontWeight: 500 }}>Month (YYYY-MM):</label>
        <input name="month" value={form.month} onChange={handleChange} required className="mj-input" placeholder="e.g. 2026-06" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Loan Balance:</label>
        <input name="loanBalance" value={form.loanBalance} onChange={handleChange} required type="number" step="0.01" className="mj-input" placeholder="e.g. 671000" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Offset Balance:</label>
        <input name="offsetBalance" value={form.offsetBalance} onChange={handleChange} required type="number" step="0.01" className="mj-input" placeholder="e.g. 291000" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Required Repayment:</label>
        <input name="requiredRepayment" value={form.requiredRepayment} onChange={handleChange} type="number" step="0.01" className="mj-input" placeholder="e.g. 4200" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Extra Repayment:</label>
        <input name="extraRepayment" value={form.extraRepayment} onChange={handleChange} type="number" step="0.01" className="mj-input" placeholder="e.g. 5000" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Interest Paid:</label>
        <input name="interestPaid" value={form.interestPaid} onChange={handleChange} type="number" step="0.01" className="mj-input" placeholder="e.g. 1710" style={{ width: '100%' }} />

        <label style={{ textAlign: 'right', fontWeight: 500 }}>Note:</label>
        <input name="note" value={form.note} onChange={handleChange} className="mj-input" placeholder="e.g. Salary plus bonus into offset" style={{ width: '100%' }} />
      </div>
      {error && <div style={{ color: '#ff6b6b', margin: '1rem 0', textAlign: 'center' }}>{error}</div>}
      <div style={{ textAlign: 'center', marginTop: 24 }}>
        <button type="submit" className="mj-btn" style={{ padding: '10px 32px', fontSize: 18, borderRadius: 8, background: 'var(--mj-accent, #1f7a73)', color: '#fff', border: 'none', cursor: 'pointer' }}>Add Month</button>
      </div>
    </form>
  );
}

function MonthlyReviewPage({ baseSnapshots, customSnapshots }) {
  return (
    <div style={{ maxWidth: 900, margin: '2rem auto' }}>
      <h2>Monthly Data Review</h2>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th>Month</th>
            <th>Loan Balance</th>
            <th>Offset Balance</th>
            <th>Required Repayment</th>
            <th>Extra Repayment</th>
            <th>Interest Paid</th>
            <th>Note</th>
            <th>Source</th>
          </tr>
        </thead>
        <tbody>
          {baseSnapshots.map((row, i) => (
            <tr key={row.month + '-base'} style={{ background: '#222', color: '#aaa' }}>
              <td>{row.month}</td>
              <td>{row.loanBalance}</td>
              <td>{row.offsetBalance}</td>
              <td>{row.requiredRepayment}</td>
              <td>{row.extraRepayment}</td>
              <td>{row.interestPaid}</td>
              <td>{row.note}</td>
              <td>Base</td>
            </tr>
          ))}
          {customSnapshots.map((row, i) => (
            <tr key={row.month + '-custom'}>
              <td>{row.month}</td>
              <td>{row.loanBalance}</td>
              <td>{row.offsetBalance}</td>
              <td>{row.requiredRepayment}</td>
              <td>{row.extraRepayment}</td>
              <td>{row.interestPaid}</td>
              <td>{row.note}</td>
              <td>Webform</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p style={{ color: '#888', marginTop: 8 }}>Base data is locked. Webform entries are appended and editable only via new submissions.</p>
    </div>
  );
}

function MortgageJourneyApp() {
  const [csvInput, setCsvInput] = useState('');
  const [customSnapshots, setCustomSnapshots] = useState([]);
  const [tab, setTab] = useState('main'); // 'main' or 'review'
  const [offsetScenarioAInput, setOffsetScenarioAInput] = useState('2000');
  const [offsetScenarioBInput, setOffsetScenarioBInput] = useState('3000');
  const [annualInterestRateInput, setAnnualInterestRateInput] = useState('6.09');
  const [minimumRepaymentInput, setMinimumRepaymentInput] = useState('4973');
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem('mortgage_journey_theme') !== 'light');
  const [unlocked, setUnlocked] = useState(() => localStorage.getItem('mortgage_journey_pin_ok') === 'true');

  const mergedData = useMemo(() => {
    if (!customSnapshots.length) {
      return mortgageData;
    }
    // Append custom snapshots to base data, sorted by month
    const all = [...mortgageData.snapshots, ...customSnapshots];
    all.sort((a, b) => (a.month > b.month ? 1 : a.month < b.month ? -1 : 0));
    return {
      ...mortgageData,
      snapshots: all,
    };
  }, [customSnapshots]);

  const series = useMemo(() => buildSeries(mergedData), [mergedData]);
  const summary = useMemo(() => buildSummary(mergedData, series), [mergedData, series]);

  const offsetScenarioAValue = useMemo(() => {
    const cleaned = offsetScenarioAInput.replace(/[^\d.]/g, '');
    const parsed = Number(cleaned);
    if (!cleaned || !Number.isFinite(parsed) || parsed < 0) {
      return null;
    }
    return parsed;
  }, [offsetScenarioAInput]);

  const offsetScenarioBValue = useMemo(() => {
    const cleaned = offsetScenarioBInput.replace(/[^\d.]/g, '');
    const parsed = Number(cleaned);
    if (!cleaned || !Number.isFinite(parsed) || parsed < 0) {
      return null;
    }
    return parsed;
  }, [offsetScenarioBInput]);

  const annualInterestRateValue = useMemo(() => {
    const cleaned = annualInterestRateInput.replace(/[^\d.]/g, '');
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed) || parsed < 0) {
      return null;
    }
    return parsed;
  }, [annualInterestRateInput]);

  const minimumRepaymentValue = useMemo(() => {
    const cleaned = minimumRepaymentInput.replace(/[^\d.]/g, '');
    const parsed = Number(cleaned);
    if (!Number.isFinite(parsed) || parsed <= 0) {
      return null;
    }
    return parsed;
  }, [minimumRepaymentInput]);

  const forecasts = useMemo(
    () =>
      buildScenarioForecasts(series, {
        offsetScenarios: [
          { id: 'no-growth', label: 'No Growth', color: '#aa5d27', offsetGain: 0 },
          {
            id: 'scenario-a',
            label: offsetScenarioAValue !== null ? `Realistic (${formatMoney(offsetScenarioAValue, mergedData.currency)})` : 'Realistic (optional)',
            color: '#1f7a73',
            offsetGain: offsetScenarioAValue ?? 0,
          },
          {
            id: 'scenario-b',
            label: offsetScenarioBValue !== null ? `Stretch (${formatMoney(offsetScenarioBValue, mergedData.currency)})` : 'Stretch (optional)',
            color: '#2b4f97',
            offsetGain: offsetScenarioBValue ?? 0,
          },
        ],
        annualInterestRatePct: annualInterestRateValue,
        minimumMonthlyRepayment: minimumRepaymentValue,
      }),
    [
      series,
      offsetScenarioAValue,
      offsetScenarioBValue,
      mergedData.currency,
      annualInterestRateValue,
      minimumRepaymentValue,
    ]
  );

  const realisticScenario = useMemo(
    () => forecasts.find((scenario) => scenario.id === 'scenario-a') || forecasts[0] || null,
    [forecasts]
  );

  const realisticPayoffLabel = useMemo(() => {
    if (!realisticScenario?.payoffMonth) {
      return 'date not reached in current horizon';
    }
    const [year, month] = realisticScenario.payoffMonth.split('-').map(Number);
    const date = new Date(year, month - 1, 1);
    return date.toLocaleDateString('en-AU', { month: 'long', year: 'numeric' });
  }, [realisticScenario]);

  const monthlyOffsetGrowth = useMemo(() => {
    if (series.length < 2) {
      return 0;
    }
    const last = series[series.length - 1];
    const prev = series[series.length - 2];
    return Math.max(last.offsetBalance - prev.offsetBalance, 0);
  }, [series]);

  const monthlyTarget = 2000;
  const monthlyTargetPct = Math.min((monthlyOffsetGrowth / monthlyTarget) * 100, 100);

  const targetStatus = useMemo(() => {
    if (monthlyOffsetGrowth >= monthlyTarget * 1.15) {
      return {
        label: 'Ahead',
        tone: 'ahead',
      };
    }
    if (monthlyOffsetGrowth >= monthlyTarget) {
      return {
        label: 'On Track',
        tone: 'ontrack',
      };
    }
    return {
      label: 'Behind',
      tone: 'behind',
    };
  }, [monthlyOffsetGrowth, monthlyTarget]);

  const targetGap = monthlyOffsetGrowth - monthlyTarget;

  const currentAnnualRate = annualInterestRateValue ?? 6.09;
  const projectedAnnualInterestNoOffset = summary.last.loanBalance * (currentAnnualRate / 100);
  const projectedAnnualInterestWithOffset = Math.max(summary.last.loanBalance - summary.last.offsetBalance, 0) * (currentAnnualRate / 100);
  const projectedAnnualInterestSaved = Math.max(projectedAnnualInterestNoOffset - projectedAnnualInterestWithOffset, 0);

  const cumulativeInterestSaved = useMemo(() => {
    return series.reduce((sum, row) => {
      const monthlySaving = row.offsetBalance * (currentAnnualRate / 100 / 12);
      return sum + Math.max(monthlySaving, 0);
    }, 0);
  }, [series, currentAnnualRate]);

  useEffect(() => {
    localStorage.setItem('mortgage_journey_theme', darkMode ? 'dark' : 'light');
  }, [darkMode]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Mortgage Journey (Private Dev)';

    let robots = document.querySelector('meta[name="robots"]');
    const created = !robots;
    if (!robots) {
      robots = document.createElement('meta');
      robots.setAttribute('name', 'robots');
      document.head.appendChild(robots);
    }
    const previousRobots = robots.getAttribute('content');
    robots.setAttribute('content', 'noindex, nofollow, noarchive');

    return () => {
      document.title = previousTitle;
      if (created) {
        robots.remove();
      } else if (previousRobots) {
        robots.setAttribute('content', previousRobots);
      }
    };
  }, []);

  const applyCsv = () => {
    const parsed = parseCsvSnapshots(csvInput);
    if (parsed.length) {
      setCustomSnapshots(parsed);
    }
  };

  const handleAddMonth = (entry) => {
    setCustomSnapshots((prev) => {
      // Prevent duplicate months from webform
      if (prev.some((snap) => snap.month === entry.month)) {
        return prev.map((snap) => (snap.month === entry.month ? entry : snap));
      }
      return [...prev, entry];
    });
  };

  const resetCsv = () => {
    setCustomSnapshots([]);
    setCsvInput('');
  };

  if (!unlocked) {
    return <PinGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className={`mj-shell ${darkMode ? 'mj-shell--dark' : 'mj-shell--light'}`}>
      <nav style={{ display: 'flex', gap: 16, margin: '1rem 0 0 1rem' }}>
        <button onClick={() => setTab('main')} style={{ fontWeight: tab === 'main' ? 'bold' : 'normal' }}>Dashboard</button>
        <button onClick={() => setTab('review')} style={{ fontWeight: tab === 'review' ? 'bold' : 'normal' }}>Monthly Entry & Review</button>
      </nav>
      {tab === 'review' ? (
        <>
          <MonthlyEntryForm onAdd={handleAddMonth} />
          <MonthlyReviewPage baseSnapshots={mortgageData.snapshots} customSnapshots={customSnapshots} />
        </>
      ) : (
        <>
          <header className="mj-hero mj-reveal" style={{ '--mj-delay': '0.03s' }}>
            <div>
              <div className="mj-heroTopRow">
                <p className="mj-kicker">Private Wealth Tracker</p>
                <button
                  className="mj-themeToggle"
                  type="button"
                  onClick={() => setDarkMode((value) => !value)}
                  aria-label="Toggle dark mode"
                >
                  {darkMode ? 'Light Mode' : 'Dark Mode'}
                </button>
              </div>
              <h1>{mergedData.household}</h1>
              <p className="mj-heroMessage">
                You are {summary.effectiveProgressPct.toFixed(1)}% of the way to full offset and on track to clear by {realisticPayoffLabel} on the Realistic scenario.
              </p>
            </div>
            <div className="mj-hero__numbers">
              <div>
                <span>Current loan</span>
                <strong>{formatMoney(summary.last.loanBalance, mergedData.currency)}</strong>
              </div>
              <div>
                <span>Current offset</span>
                <strong>{formatMoney(summary.last.offsetBalance, mergedData.currency)}</strong>
              </div>
              <div>
                <span>Effective balance</span>
                <strong>{formatMoney(summary.last.effectiveBalance, mergedData.currency)}</strong>
              </div>
              <div className="mj-hero__payoff">
                <span>On track to clear</span>
                <strong>{realisticPayoffLabel}</strong>
              </div>
            </div>
          </header>

          <section className="mj-kpiStrip mj-reveal" style={{ '--mj-delay': '0.08s' }}>
            <article>
              <p>{formatMonthLabel(summary.last.month)} target tracker</p>
              <div className="mj-kpiTrackerRow">
                <span className={`mj-statusChip mj-statusChip--${targetStatus.tone}`}>{targetStatus.label}</span>
                <small>{targetGap >= 0 ? `+${formatMoney(targetGap, mergedData.currency)}` : `${formatMoney(targetGap, mergedData.currency)}`}</small>
              </div>
              <h3>{formatMoney(monthlyOffsetGrowth, mergedData.currency)} / {formatMoney(monthlyTarget, mergedData.currency)}</h3>
              <div className="mj-kpiBar"><span style={{ width: `${monthlyTargetPct}%` }} /></div>
            </article>
            <article>
              <p>Cumulative interest avoided</p>
              <h3>{formatMoney(cumulativeInterestSaved, mergedData.currency)}</h3>
              <small>Estimated using current annual rate of {currentAnnualRate.toFixed(2)}%</small>
            </article>
            <article>
              <p>Annual interest projection</p>
              <h3>{formatMoney(projectedAnnualInterestWithOffset, mergedData.currency)}</h3>
              <small>
                Without offset: {formatMoney(projectedAnnualInterestNoOffset, mergedData.currency)}. Saving {formatMoney(projectedAnnualInterestSaved, mergedData.currency)} / year.
              </small>
            </article>
          </section>

          <section className="mj-rings mj-reveal" style={{ '--mj-delay': '0.12s' }}>
            <ScoreRing value={summary.effectiveProgressPct} label="Effective Paydown" tone="effective" />
            <ScoreRing value={summary.actualPaydownPct} label="Actual Paydown" tone="principal" />
            <ScoreRing value={summary.offsetCoveragePct} label="Offset Coverage" tone="offset" />
          </section>

          <section className="mj-metrics mj-reveal" style={{ '--mj-delay': '0.16s' }}>
            <article>
              <p>Starting Mortgage Balance</p>
              <h3>{formatMoney(mergedData.startPrincipal, mergedData.currency)}</h3>
            </article>
            <article>
              <p>Total Principal Paid Down</p>
              <h3>{formatMoney(summary.totalPrincipalPaidDown, mergedData.currency)}</h3>
            </article>
            <article>
              <p>Total Offset Grown</p>
              <h3>{formatMoney(summary.totalOffsetGrown, mergedData.currency)}</h3>
            </article>
            <article>
              <p>Effective Paid (including offset)</p>
              <h3>{formatMoney(summary.effectivePaid, mergedData.currency)}</h3>
            </article>
            <article>
              <p>Total Interest Logged</p>
              <h3>{formatMoney(summary.totalInterest, mergedData.currency)}</h3>
            </article>
            <article>
              <p>Total Extra Repayments</p>
              <h3>{formatMoney(summary.totalExtra, mergedData.currency)}</h3>
            </article>
          </section>

          <section className="mj-grid2 mj-reveal" style={{ '--mj-delay': '0.2s' }}>
            <BalanceTrendChart series={series} currency={mergedData.currency} />
            <MonthlyImpactChart series={series} currency={mergedData.currency} />
          </section>

          <section className="mj-forecastWrap mj-reveal" style={{ '--mj-delay': '0.24s' }}>
            <div className="mj-forecastControl">
              <div>
                <h3>Monthly Offset Scenarios</h3>
                <p>
                  Compare three futures: no offset growth, plus two optional growth scenarios.
                </p>
              </div>
              <div className="mj-forecastControl__inputs">
                <div className="mj-offsetScenarioGrid">
                  <label className="mj-offsetScenarioBox">
                    <span>No growth scenario</span>
                    <input type="text" value="0" disabled aria-label="No growth offset scenario" />
                  </label>
                  <label className="mj-offsetScenarioBox">
                    <span>Optional scenario A</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={offsetScenarioAInput}
                      onChange={(e) => setOffsetScenarioAInput(e.target.value)}
                      placeholder="e.g. 1500"
                      aria-label="Monthly offset scenario A"
                    />
                  </label>
                  <label className="mj-offsetScenarioBox">
                    <span>Optional scenario B</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={offsetScenarioBInput}
                      onChange={(e) => setOffsetScenarioBInput(e.target.value)}
                      placeholder="e.g. 3000"
                      aria-label="Monthly offset scenario B"
                    />
                  </label>
                </div>

                <input
                  type="text"
                  inputMode="decimal"
                  value={annualInterestRateInput}
                  onChange={(e) => setAnnualInterestRateInput(e.target.value)}
                  placeholder="Annual interest rate % (e.g. 6.14)"
                  aria-label="Annual interest rate"
                />
                <input
                  type="text"
                  inputMode="decimal"
                  value={minimumRepaymentInput}
                  onChange={(e) => setMinimumRepaymentInput(e.target.value)}
                  placeholder="Minimum monthly repayment"
                  aria-label="Minimum monthly repayment"
                />
                <small>
                  Scenario A uses {formatMoney(offsetScenarioAValue ?? 0, mergedData.currency)} / month. Scenario B uses {formatMoney(offsetScenarioBValue ?? 0, mergedData.currency)} / month.
                </small>
                <small>
                  {annualInterestRateValue !== null && minimumRepaymentValue !== null
                    ? `Finance model active: ${annualInterestRateValue.toFixed(2)}% p.a. daily compounding, minimum repayment ${formatMoney(minimumRepaymentValue, mergedData.currency)}.`
                    : 'Enter interest rate and minimum repayment to use finance-model forecasting.'}
                </small>
              </div>
            </div>

            <ForecastChart scenarios={forecasts} currency={mergedData.currency} />
            <div className="mj-forecastCards">
              {forecasts.map((scenario) => (
                <article key={scenario.id} className={`mj-forecastCard mj-forecastCard--${scenario.id}`}>
                  <p>{scenario.label}</p>
                  <h3>{scenario.payoffMonth ? new Date(`${scenario.payoffMonth}-01`).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' }) : 'Not reached'}</h3>
                  <span>{scenario.monthsToFullyOffset ? `${scenario.monthsToFullyOffset} months to full offset` : 'Forecast horizon exceeded'}</span>
                  <small>
                    Principal drop: {formatMoney(scenario.principalDrop, mergedData.currency)} / month
                  </small>
                  <small>
                    Offset growth: {formatMoney(scenario.offsetGain, mergedData.currency)} / month
                  </small>
                  {scenario.useInterestModel ? (
                    <small>
                      Repayment model: {formatMoney(scenario.scenarioMonthlyRepayment, mergedData.currency)} / month @ {scenario.annualInterestRatePct.toFixed(2)}% p.a.
                    </small>
                  ) : null}
                </article>
              ))}
            </div>
          </section>

          <section className="mj-story mj-reveal" style={{ '--mj-delay': '0.28s' }}>
            <h2>Journey Milestones</h2>
            <div className="mj-story__timeline">
              {mergedData.storyEvents.map((event) => (
                <div key={`${event.date}-${event.title}`} className="mj-milestone">
                  <div className="mj-milestone__date">
                    {new Date(event.date).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                  </div>
                  <div className="mj-milestone__icon"><MilestoneIcon type={event.type} /></div>
                  <article>
                    <h3>{event.title}</h3>
                    <p>{event.description}</p>
                    {event.amount ? <strong>{formatMoney(event.amount, mergedData.currency)}</strong> : null}
                  </article>
                </div>
              ))}
            </div>
          </section>

          <section className="mj-feed mj-reveal" style={{ '--mj-delay': '0.32s' }}>
            <h2>Drop In Fresh Data (Legacy)</h2>
            <p>
              Paste CSV using: month,loanBalance,offsetBalance,requiredRepayment,extraRepayment,interestPaid,note.
            </p>
            <textarea
              value={csvInput}
              onChange={(e) => setCsvInput(e.target.value)}
              placeholder={'month,loanBalance,offsetBalance,requiredRepayment,extraRepayment,interestPaid,note\n2026-05,671000,291000,4200,5000,1710,Salary plus bonus into offset'}
            />
            <div className="mj-feed__actions">
              <button onClick={applyCsv}>Apply CSV</button>
              <button onClick={resetCsv} className="ghost">Reset to Base Data</button>
            </div>
            <p style={{ color: '#888', marginTop: 8 }}>Prefer the Monthly Entry & Review tab for new data going forward.</p>
          </section>
        </>
      )}
    </div>
  );
}

export default MortgageJourneyApp;